import React from 'react'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { Checkbox } from './Checkbox'

import './TreeSelect.scss'

/**
 * Renders a recursive tree of nodes with depth-based indentation.
 * Supports multi-select and single-select use cases — the calling component controls selection
 * logic via `selectedIDs` and `onNodeClick`.
 * When `onNodeClick` is provided, each node displays a checkbox and is interactive.
 * When omitted, the tree renders in read-only mode without checkboxes.
 *
 * @import import { TreeSelect } from '@/components/forms/TreeSelect'
 * @param {object} props
 * @param {object} [props.data] Root tree node with nested `children` arrays. Renders a no-results message when falsy.
 * @param {Array<string>} [props.selectedIDs] IDs of currently selected nodes.
 * @param {Array<string>} [props.disabledIDs] IDs of nodes that cannot be toggled.
 * @param {boolean} [props.disabled] Disables the entire tree.
 * @param {(node: object) => void} [props.onNodeClick] Called with the clicked node. Omit for read-only display.
 * @param {string} props.idAccessor Key used to read each node's unique ID from each data entry.
 * @param {string} props.labelAccessor Key used to read each node's display label from each data entry.
 * @param {string} [props.noResultsMessage] Text shown when `data` is falsy.
 * @param {string} [props.noResultsTestId] data-testid for the no-results element.
 */
export const TreeSelect = ({
  data,
  selectedIDs = [],
  disabledIDs = [],
  disabled = false,
  onNodeClick,
  idAccessor,
  labelAccessor,
  noResultsMessage = 'No results found',
  noResultsTestId,
}) => {
  if (!data) {
    return (
      <div className="tree-select-no-results" data-testid={noResultsTestId}>
        {noResultsMessage}
      </div>
    )
  }

  const renderNode = (node, depth = 0) => {
    const nodeId = node[idAccessor]
    const isChecked = selectedIDs.includes(nodeId)
    const nodeDisabled = disabledIDs.includes(nodeId)

    return (
      <li key={nodeId}>
        <div
          className={classNames('tree-select-node', { checked: isChecked, disabled: nodeDisabled })}
          style={{ paddingLeft: `${16 + depth * 16}px` }}
          onClick={() => {
            if (onNodeClick && !nodeDisabled && !disabled) {
              onNodeClick(node)
            }
          }}
        >
          {/* Named rather than hidden: clicking or pressing Space here bubbles to the row's
              onClick, so this is the real affordance and its state must be perceivable. */}
          {onNodeClick && (
            <Checkbox
              aria-label={node[labelAccessor]}
              checked={isChecked}
              disabled={nodeDisabled || disabled}
            />
          )}
          {node[labelAccessor]}
        </div>
        {!isEmpty(node.children) && (
          <ul>{node.children.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    )
  }

  return <ul className="tree-select-list">{renderNode(data)}</ul>
}
